import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, IconButton, Alert, Switch, FormControlLabel,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { powerAddonsAPI } from '../../services/api';
import ConfirmDialog from '../../components/ConfirmDialog';
import { PowerAddon } from '../../types';

interface FormData {
  powerAddonName: string;
  description: string;
  displayOrder: number;
  isActive: boolean;
  isOneTime: boolean;
}

const PowerAddons = () => {
  const [items, setItems] = useState<PowerAddon[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editing, setEditing] = useState<PowerAddon | null>(null);
  const [formData, setFormData] = useState<FormData>({ powerAddonName: '', description: '', displayOrder: 0, isActive: true, isOneTime: false });
  const [confirmDialog, setConfirmDialog] = useState<{ open: boolean; id: number }>({ open: false, id: 0 });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try { const response = await powerAddonsAPI.getAll(); setItems(response.data); setError(''); }
    catch { setError('Failed to load power addons'); } finally { setLoading(false); }
  };

  const handleOpenDialog = (item: PowerAddon | null = null) => {
    if (item) {
      setEditing(item);
      setFormData({ powerAddonName: item.PowerAddonName, description: item.Description || '', displayOrder: item.DisplayOrder, isActive: !!item.IsActive, isOneTime: !!item.IsOneTime });
    } else {
      setEditing(null);
      setFormData({ powerAddonName: '', description: '', displayOrder: 0, isActive: true, isOneTime: false });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => { setOpenDialog(false); setEditing(null); };

  const handleSubmit = async () => {
    try {
      if (editing) { await powerAddonsAPI.update(editing.PowerAddonId, formData); }
      else { await powerAddonsAPI.create(formData); }
      fetchData(); handleCloseDialog(); setError('');
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      setError(axiosErr.response?.data?.error || 'Failed to save power addon');
    }
  };

  const handleDeleteConfirm = async () => {
    const id = confirmDialog.id;
    setConfirmDialog({ open: false, id: 0 });
    try { await powerAddonsAPI.delete(id); fetchData(); } catch { setError('Failed to delete power addon'); }
  };

  if (loading) return <Typography>Loading...</Typography>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">Power Addons</Typography>
        <Button variant="contained" color="secondary" size="small" startIcon={<AddIcon fontSize="small" />} onClick={() => handleOpenDialog()}>Add Power Addon</Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead><TableRow>
            <TableCell>Name</TableCell><TableCell>Description</TableCell><TableCell>Display Order</TableCell><TableCell>One-time</TableCell><TableCell>Active</TableCell><TableCell align="right">Actions</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {items.map((item) => (
              <TableRow key={item.PowerAddonId}>
                <TableCell>{item.PowerAddonName}</TableCell><TableCell>{item.Description || '-'}</TableCell>
                <TableCell>{item.DisplayOrder}</TableCell><TableCell>{item.IsOneTime ? 'Yes' : 'No'}</TableCell><TableCell>{item.IsActive ? 'Yes' : 'No'}</TableCell>
                <TableCell align="right">
                  <IconButton onClick={() => handleOpenDialog(item)} size="small"><EditIcon fontSize="small" /></IconButton>
                  <IconButton onClick={() => setConfirmDialog({ open: true, id: item.PowerAddonId })} size="small" color="error"><DeleteIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Power Addon' : 'Add Power Addon'}</DialogTitle>
        <DialogContent>
          <TextField size="small" autoFocus margin="dense" label="Name" fullWidth value={formData.powerAddonName} onChange={(e) => setFormData({ ...formData, powerAddonName: e.target.value })} required />
          <TextField size="small" margin="dense" label="Description" fullWidth multiline rows={3} value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
          <TextField size="small" margin="dense" label="Display Order" type="number" fullWidth value={formData.displayOrder} onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 0 })} />
          <FormControlLabel control={<Switch checked={formData.isOneTime} onChange={(e) => setFormData({ ...formData, isOneTime: e.target.checked })} />} label="One-time Build Cost" />
          <FormControlLabel control={<Switch checked={formData.isActive} onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })} />} label="Active" />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} size="small">Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="secondary" disabled={!formData.powerAddonName}>{editing ? 'Update' : 'Create'}</Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog open={confirmDialog.open} title="Deactivate Power Addon" message="Are you sure you want to deactivate this power addon?" confirmLabel="Deactivate" confirmColor="error" onConfirm={handleDeleteConfirm} onCancel={() => setConfirmDialog({ open: false, id: 0 })} />
    </Box>
  );
};

export default PowerAddons;
